import { Check, Sparkles } from "lucide-react";
import { Reveal, scrollToId } from "@/components/Reveal";

const PACKAGES = [
  {
    id: "essentials",
    name: "The Essentials",
    tagline: "The full bar experience, minus the bar.",
    price: "$500",
    guests: "Serves up to 50 guests",
    rule: "75+ guests requires a 2nd bartender",
    features: [
      "TABC certified & compliant bartenders",
      "Basic mixers & soda",
      "Fresh garnish station",
      "Glassware for your guests",
      "Full bar tools & equipment",
    ],
    featured: false,
  },
  {
    id: "full_pour",
    name: "The Full Pour",
    tagline: "Everything in The Essentials — plus the showpieces.",
    price: "$800",
    guests: "Serves up to 70 guests",
    rule: "100+ guests requires a 2nd bartender",
    features: [
      "Everything in The Essentials",
      "Premium mixers & sodas",
      "3 signature cocktails crafted for your event",
      "Styled setup that steals the scene",
    ],
    featured: true,
  },
  {
    id: "zero_proof",
    name: "The Zero Proof",
    tagline: "A Sip for Every Kind of Celebration!",
    price: "$400",
    guests: "Serves up to 50 guests",
    rule: "75+ guests requires a 2nd bartender",
    features: [
      "TABC certified & compliant bartenders",
      "Crafted mocktail menu — matcha, tea infusions & lemonades",
      "Fresh-pressed mixers & garnish station",
      "Glassware for your guests",
      "Full bar tools & equipment",
    ],
    featured: false,
  },
];

export default function Packages() {
  return (
    <section
      id="packages"
      data-testid="packages-section"
      className="grain bg-espresso-deep px-5 py-24 sm:px-8 lg:py-32"
    >
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <Reveal>
              <span className="eyebrow text-gold">Packages</span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-4xl text-cream sm:text-5xl lg:text-6xl">
                Pick your <em className="italic text-rose">pour</em>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="max-w-sm text-sm leading-relaxed text-cream/60 sm:text-base">
              Every package includes setup and breakdown, with a 5-hour minimum. Final
              pricing depends on the details — grab a quote and we'll make it make sense.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.15} className="h-full">
              <div
                data-testid={`package-card-${p.id}`}
                className={`relative flex h-full flex-col rounded-3xl border p-8 transition-all duration-300 hover:-translate-y-1.5 sm:p-10 ${
                  p.featured
                    ? "border-gold/60 bg-gradient-to-b from-blush/[0.07] to-transparent shadow-[0_30px_60px_-30px_rgba(212,175,55,0.25)]"
                    : "border-cream/15 bg-white/[0.03] hover:border-cream/30"
                }`}
              >
                {p.featured && (
                  <span
                    data-testid={`package-badge-${p.id}`}
                    className="absolute -top-3.5 left-8 inline-flex items-center gap-1.5 rounded-full bg-gold px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-espresso"
                  >
                    <Sparkles className="h-3 w-3" /> Most popular
                  </span>
                )}

                <h3 className="font-display text-3xl text-cream sm:text-4xl">{p.name}</h3>
                <p className="mt-2 text-sm text-cream/60">{p.tagline}</p>

                <p className="mt-6 flex items-baseline gap-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-cream/50">from</span>
                  <span className="font-display text-5xl text-gold" data-testid={`package-price-${p.id}`}>
                    {p.price}
                  </span>
                  <span className="ml-2 text-sm font-medium text-cream/70" data-testid={`package-guests-${p.id}`}>
                    · {p.guests}
                  </span>
                </p>

                <ul className="mt-8 flex-1 space-y-3.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-cream/85 sm:text-base">
                      <span
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                          p.featured ? "bg-gold text-espresso" : "bg-cream/15 text-cream"
                        }`}
                      >
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                <p className="mt-5 text-[11px] tracking-wide text-cream/45" data-testid={`package-rule-${p.id}`}>
                  {p.rule}
                </p>

                <button
                  data-testid={`package-cta-${p.id}`}
                  onClick={() => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" })}
                  className={`mt-6 rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] transition-all duration-300 hover:scale-[1.02] ${
                    p.featured
                      ? "bg-gold text-espresso hover:bg-cream"
                      : "border border-cream/40 text-cream hover:border-gold hover:text-gold"
                  }`}
                >
                  Book your date
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}